import React, { useState, useEffect } from 'react';
import { ThemePrimaryButton } from '../button';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Avatar,
  Chip,
  CircularProgress
} from '@mui/material';
import { PersonOutline, AdminPanelSettings, Support, ManageAccounts } from '@mui/icons-material';
import axios from 'axios';
import PageSnackbars from '../common/PageSnackbars';
import { ROLE_ADMIN, ROLE_MANAGER, ROLE_SUPPORT, normalizeRole } from '../../utils/userRoles';

const UserSelector = ({ currentUser, initialUserId, onUserSwitch, onClose }) => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(initialUserId || currentUser?.id || '');
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Получение списка пользователей
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setFetchingUsers(true);
        const response = await axios.get('/api/auth/users-for-impersonation', {
          withCredentials: true
        });
        setUsers(response.data.users);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to load users list');
      } finally {
        setFetchingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    if (initialUserId) {
      setSelectedUserId(initialUserId);
    }
  }, [initialUserId]);

  // Переключение пользователя
  const handleUserSwitch = async () => {
    const currentUserId = currentUser?.id || currentUser?._id;
    if (!selectedUserId || selectedUserId === currentUserId) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/auth/impersonate', {
        userId: selectedUserId
      }, {
        withCredentials: true
      });

      setSuccess('Successfully switched user');
      onUserSwitch(response.data.user);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error switching user:', error);
      setError(error.response?.data?.error || 'Failed to switch user');
    } finally {
      setLoading(false);
    }
  };

  // Получение иконки роли
  const getRoleIcon = (role) => {
    switch (normalizeRole(role)) {
      case ROLE_ADMIN:
        return <AdminPanelSettings fontSize="small" />;
      case ROLE_MANAGER:
        return <ManageAccounts fontSize="small" />;
      case ROLE_SUPPORT:
        return <Support fontSize="small" />;
      default:
        return <PersonOutline fontSize="small" />;
    }
  };

  // Получение цвета роли
  const getRoleColor = (role) => {
    switch (normalizeRole(role)) {
      case ROLE_ADMIN:
        return 'error';
      case ROLE_MANAGER:
        return 'warning';
      case ROLE_SUPPORT:
        return 'info';
      default:
        return 'default';
    }
  };

  // Получение имени пользователя
  const getUserDisplayName = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.login;
  };

  if (fetchingUsers) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minWidth: 400 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Switch User Account
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select a user account to switch to. You can return to your original account at any time.
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Select User</InputLabel>
        <Select
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          label="Select User"
        >
          {users.map((user) => (
            <MenuItem key={user._id} value={user._id}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar sx={{ width: 32, height: 32, mr: 2 }}>
                  {getUserDisplayName(user)[0]?.toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1">
                    {getUserDisplayName(user)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
                <Chip
                  icon={getRoleIcon(user.role)}
                  label={normalizeRole(user.role).toUpperCase()}
                  color={getRoleColor(user.role)}
                  size="small"
                />
                {user._id === (currentUser?.id || currentUser?._id) && (
                  <Chip
                    label="Current"
                    color="primary"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <ThemePrimaryButton
          onClick={handleUserSwitch}
          disabled={loading || !selectedUserId || selectedUserId === (currentUser?.id || currentUser?._id)}
        >
          {loading ? <CircularProgress size={20} /> : 'Switch User'}
        </ThemePrimaryButton>
      </Box>

      <PageSnackbars
        successMessage={success}
        errorMessage={error}
        onCloseSuccess={() => setSuccess('')}
        onCloseError={() => setError('')}
        successAutoHideDuration={3000}
      />
    </Box>
  );
};

export default UserSelector; 